// ProductStep - Product/equipment identification for worksheet
import { Button, Card, Input } from '@/components/ui';
import { useProducts } from '@/hooks/use-products';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useMemo, useState } from 'react';
import { isUnderWarranty } from '../mock-data';
import type { Product } from '../types';
import { CATEGORY_LABEL_KEYS, ProductCategory } from '../types';
import { useWorksheetWizardStore } from '../wizard-store';

/** Map API product to local wizard Product type */
function toWizardProduct(p: {
  id: string;
  name: string;
  brand?: string;
  model?: string;
  sku?: string;
  category?: string;
}): Product {
  return {
    id: p.id,
    name: p.name,
    brand: p.brand,
    model: p.model,
    serialNumber: p.sku,
    category: (p.category as ProductCategory) ?? ProductCategory.OTHER,
  };
}

export function ProductStep() {
  const { t } = useTranslation();
  const { product, deviceName, serialNumber, setProduct, setDeviceInfo } =
    useWorksheetWizardStore();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ProductCategory | 'ALL'>('ALL');
  const [manualEntry, setManualEntry] = useState(false);
  const { products: apiProducts, isLoading, error } = useProducts();

  const products = useMemo(() => apiProducts.map(toWizardProduct), [apiProducts]);

  const filteredProducts = useMemo(() => {
    let filtered = products;

    if (categoryFilter !== 'ALL') {
      filtered = filtered.filter(p => p.category === categoryFilter);
    }

    if (search.trim()) {
      const term = search.toLowerCase();
      filtered = filtered.filter(
        p =>
          p.name.toLowerCase().includes(term) ||
          p.brand?.toLowerCase().includes(term) ||
          p.model?.toLowerCase().includes(term) ||
          p.serialNumber?.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [search, categoryFilter, products]);

  const handleSelectProduct = (p: Product) => {
    setProduct(p);
    setManualEntry(false);
  };

  const handleManualEntry = () => {
    setProduct(null);
    setManualEntry(true);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('service.identifyDevice')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-[#ccfbf1]">
          {t('service.identifyDeviceDesc')}
        </p>
      </div>

      {/* Toggle between search and manual */}
      <div className="flex gap-2">
        <Button
          variant={!manualEntry ? 'default' : 'outline'}
          onClick={() => setManualEntry(false)}
          size="sm"
        >
          {t('service.searchInSystem')}
        </Button>
        <Button variant={manualEntry ? 'default' : 'outline'} onClick={handleManualEntry} size="sm">
          {t('service.manualEntry')}
        </Button>
      </div>

      {!manualEntry ? (
        <>
          {/* Search and filter */}
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input
              placeholder={t('service.searchProductPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1"
            />
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value as ProductCategory | 'ALL')}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-[#2d7d8a] dark:bg-[#1a4f5c] dark:text-white"
            >
              <option value="ALL">{t('page.allCategories')}</option>
              {Object.entries(CATEGORY_LABEL_KEYS).map(([value, labelKey]) => (
                <option key={value} value={value}>
                  {t(labelKey)}
                </option>
              ))}
            </select>
          </div>

          {/* Loading state */}
          {isLoading && (
            <div className="py-8 text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
              <p className="mt-2 text-sm text-gray-500 dark:text-[#ccfbf1]">
                {t('service.productsLoading')}
              </p>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
              <p className="text-sm text-red-800 dark:text-red-300">
                {t('service.productsLoadError', { message: error })}
              </p>
            </div>
          )}

          {/* Product list */}
          <div className="grid gap-3 sm:grid-cols-2">
            {filteredProducts.map(p => {
              const underWarranty = isUnderWarranty(p);
              return (
                <Card
                  key={p.id}
                  className={cn(
                    'cursor-pointer p-4 transition-all hover:shadow-md',
                    product?.id === p.id && 'ring-2 ring-primary bg-primary/5'
                  )}
                  onClick={() => handleSelectProduct(p)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900 dark:text-white">{p.name}</h3>
                        {underWarranty && (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/20 dark:text-green-300">
                            {t('service.warranty')}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-gray-500 dark:text-[#ccfbf1]">
                        {p.brand} {p.model}
                      </p>
                      {p.serialNumber && (
                        <p className="text-xs text-gray-400 dark:text-[#99f6e4]">
                          {t('service.serialNumberShort', { serial: p.serialNumber })}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-gray-400 dark:text-[#99f6e4]">
                        {t(CATEGORY_LABEL_KEYS[p.category] ?? p.category)}
                      </p>
                    </div>
                    {product?.id === p.id && (
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white">
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>

          {!isLoading && filteredProducts.length === 0 && (
            <div className="py-8 text-center text-gray-500 dark:text-[#ccfbf1]">
              <p>{t('page.noResults')}</p>
              <Button variant="link" onClick={handleManualEntry} className="mt-2">
                {t('service.manualDeviceEntry')}
              </Button>
            </div>
          )}
        </>
      ) : (
        /* Manual entry form */
        <Card className="p-4">
          <h3 className="mb-4 font-medium text-gray-900 dark:text-white">
            {t('service.manualDeviceDataEntry')}
          </h3>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                {t('service.deviceNameRequired')}
              </label>
              <Input
                placeholder={t('service.deviceNamePlaceholder')}
                value={deviceName}
                onChange={e => setDeviceInfo(e.target.value, serialNumber)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                {t('service.serialNumber')}
              </label>
              <Input
                placeholder={t('service.serialNumberPlaceholder')}
                value={serialNumber}
                onChange={e => setDeviceInfo(deviceName, e.target.value)}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Selected product summary */}
      {(product || (manualEntry && deviceName)) && (
        <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
          <div className="flex items-center gap-2">
            <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-medium text-green-800 dark:text-green-300">
              {t('service.selectedDevice', { name: product?.name ?? deviceName })}
              {(product?.serialNumber ?? serialNumber) && (
                <span className="ml-2 text-sm font-normal">
                  (
                  {t('service.serialNumberShort', {
                    serial: product?.serialNumber ?? serialNumber,
                  })}
                  )
                </span>
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
